// Asset transactions HTTP service — all backend calls for the grid and the
// booking form. URLs come from endpoints.ts; every response is validated with
// its Zod schema before it reaches Redux / component state.

import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  AssetTransactionRecord,
  validateAssetTransactionRecord
} from '../../../state_manager/data_models/asset_transaction_record.ts'
import {
  AssetTransactionDetail,
  AssetTransactionSavePayload,
  AssetTransactionHeaderOptions,
  AssetTransactionCompanyOptions,
  CreateSecurityPayload,
  ValuationScopeCurrency,
  ValuationScopeCurrencySchema,
  validateAssetTransactionDetail,
  validateAssetTransactionHeaderOptions,
  validateAssetTransactionCompanyOptions
} from '../../../state_manager/data_models/asset_transaction_detail.ts'
import { FilterValue, SortConfig } from '../../global_configs/tableViewPreferences.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

export interface AssetTransactionsPaginatedResponse {
  data: AssetTransactionRecord[]
  hasMore: boolean
  totalCount: number
  error?: string
}

export interface AssetTransactionDetailResponse {
  data: AssetTransactionDetail | null
  error?: string
}

export interface AssetTransactionActionResponse {
  data: Record<string, any> | null
  error?: string
}

function authHeaders(accessToken: string | null): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

export const fetchAssetTransactionsFiltered = async (
  sorting: SortConfig[] = [],
  filters: FilterValue[] = [],
  page: number = 1,
  pageSize: number | null = null,
  accessToken: string | null = null
): Promise<AssetTransactionsPaginatedResponse> => {
  try {
    const requestBody = {
      data: {
        page,
        page_size: pageSize ?? 200,
        sorting: sorting.map((s) => ({ key: s.key, direction: s.direction })),
        filters: filters.map((f) => ({ key: f.key, value: f.value }))
      }
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.ASSET_TRANSACTIONS.FILTER), {
      method: API_ROUTES.ASSET_TRANSACTIONS.FILTER.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify(requestBody)
    })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch asset transactions: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown[]; has_more?: boolean; count?: number }
    const rows = Array.isArray(json.data) ? json.data : []
    return {
      data: validateAssetTransactionRecord(rows, true),
      hasMore: Boolean(json.has_more),
      totalCount: json.count ?? rows.length
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching asset transactions:', error)
    return {
      data: [],
      hasMore: false,
      totalCount: 0,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export const fetchAssetTransactionsFilterOptions = async (
  accessToken: string | null = null
): Promise<{ data: Record<string, Array<{ value: string | number; label: string }>>; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.ASSET_TRANSACTIONS.FILTER_OPTIONS), {
      method: API_ROUTES.ASSET_TRANSACTIONS.FILTER_OPTIONS.method,
      headers: authHeaders(accessToken)
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch filter options: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as {
      data?: Record<string, Array<{ value: string | number; label: string }>>
    }
    return { data: json.data ?? {} }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching asset transaction filter options:', error)
    return { data: {}, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchAssetTransactionHeaderOptions = async (
  accessToken: string | null = null
): Promise<{ data: AssetTransactionHeaderOptions | null; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.ASSET_TRANSACTIONS.OPTIONS), {
      method: API_ROUTES.ASSET_TRANSACTIONS.OPTIONS.method,
      headers: authHeaders(accessToken)
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch options: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown }
    return { data: validateAssetTransactionHeaderOptions(json.data ?? {}) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching asset transaction options:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchAssetTransactionCompanyOptions = async (
  companyId: string,
  asOf: string | null = null,
  accessToken: string | null = null
): Promise<{ data: AssetTransactionCompanyOptions | null; error?: string }> => {
  try {
    const url = buildFullUrl(
      BACKEND_BASE_URL,
      API_ROUTES.ASSET_TRANSACTIONS.COMPANY_OPTIONS,
      {},
      { company_id: companyId, ...(asOf ? { as_of: asOf } : {}) }
    )
    const response = await fetch(url, {
      method: API_ROUTES.ASSET_TRANSACTIONS.COMPANY_OPTIONS.method,
      headers: authHeaders(accessToken)
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch company options: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown }
    return { data: validateAssetTransactionCompanyOptions(json.data ?? {}) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching asset transaction company options:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/** Functional currencies of funds holding in-scope positions for a
 * valuation, as-of a date — drives the per-currency unit-value inputs. */
export const fetchValuationScopeCurrencies = async (
  companyId: string,
  params: {
    asOf?: string | null
    fundId?: string | null
    valuationTypeId?: number | null
    securityTypeId?: number | null
    securityId?: string | null
    trancheId?: string | null
  },
  accessToken: string | null = null
): Promise<{ data: ValuationScopeCurrency[] | null; error?: string }> => {
  try {
    const url = buildFullUrl(
      BACKEND_BASE_URL,
      API_ROUTES.ASSET_TRANSACTIONS.VALUATION_CURRENCIES,
      {},
      {
        company_id: companyId,
        ...(params.asOf ? { as_of: params.asOf } : {}),
        ...(params.fundId ? { fund_id: params.fundId } : {}),
        ...(params.valuationTypeId != null ? { valuation_type_id: String(params.valuationTypeId) } : {}),
        ...(params.securityTypeId != null ? { security_type_id: String(params.securityTypeId) } : {}),
        ...(params.securityId ? { security_id: params.securityId } : {}),
        ...(params.trancheId ? { tranche_id: params.trancheId } : {})
      }
    )
    const response = await fetch(url, {
      method: API_ROUTES.ASSET_TRANSACTIONS.VALUATION_CURRENCIES.method,
      headers: authHeaders(accessToken)
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch valuation currencies: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown }
    const rows = Array.isArray(json.data) ? json.data : []
    return { data: rows.map((r) => ValuationScopeCurrencySchema.parse(r)) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching valuation scope currencies:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/** Create a security from the booking form; returns the new security id. */
export const createSecurity = async (
  payload: CreateSecurityPayload,
  accessToken: string | null = null
): Promise<{ securityId: string | null; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.ASSET_TRANSACTIONS.CREATE_SECURITY), {
      method: API_ROUTES.ASSET_TRANSACTIONS.CREATE_SECURITY.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: payload })
    })

    const json = (await response.json().catch(() => ({}))) as {
      data?: { security_id?: string }
      message?: string
      error?: string
    }

    if (!response.ok) {
      throw new Error(json.message || json.error || `Failed to create security: ${response.status}`)
    }

    return { securityId: json.data?.security_id ?? null }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error creating security:', error)
    return { securityId: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchAssetTransactionDetail = async (
  transactionId: string,
  accessToken: string | null = null
): Promise<AssetTransactionDetailResponse> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.ASSET_TRANSACTIONS.GET, { id: transactionId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.ASSET_TRANSACTIONS.GET.method,
      headers: authHeaders(accessToken)
    })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      if (response.status === 404) throw new Error('Asset transaction not found')
      throw new Error(`Failed to fetch asset transaction: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown }
    if (!json.data) return { data: null }
    return { data: validateAssetTransactionDetail(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching asset transaction detail:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/**
 * Create or update a Draft asset transaction. The backend regenerates (or,
 * when customized, preserves) the journal preview and returns the full
 * refreshed detail in the same round-trip.
 */
export const saveAssetTransaction = async (
  payload: AssetTransactionSavePayload,
  accessToken: string | null = null
): Promise<AssetTransactionDetailResponse> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.ASSET_TRANSACTIONS.SAVE), {
      method: API_ROUTES.ASSET_TRANSACTIONS.SAVE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: payload })
    })

    const json = (await response.json().catch(() => ({}))) as { data?: unknown; message?: string; error?: string }

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(json.message || json.error || `Failed to save asset transaction: ${response.status}`)
    }

    if (!json.data) return { data: null }
    return { data: validateAssetTransactionDetail(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error saving asset transaction:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/** Discard user journal edits and regenerate the engine defaults. */
export const resetAssetTransactionJournals = async (
  transactionId: string,
  accessToken: string | null = null
): Promise<AssetTransactionDetailResponse> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.ASSET_TRANSACTIONS.RESET_JOURNALS, { id: transactionId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.ASSET_TRANSACTIONS.RESET_JOURNALS.method,
      headers: authHeaders(accessToken)
    })

    const json = (await response.json().catch(() => ({}))) as { data?: unknown; message?: string; error?: string }

    if (!response.ok) {
      throw new Error(json.message || json.error || `Failed to reset journals: ${response.status}`)
    }

    if (!json.data) return { data: null }
    return { data: validateAssetTransactionDetail(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error resetting asset transaction journals:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const bookAssetTransaction = async (
  transactionId: string,
  accessToken: string | null = null
): Promise<AssetTransactionActionResponse> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.ASSET_TRANSACTIONS.BOOK, { id: transactionId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.ASSET_TRANSACTIONS.BOOK.method,
      headers: authHeaders(accessToken)
    })
    const json = (await response.json().catch(() => ({}))) as {
      data?: Record<string, any>
      message?: string
      error?: string
    }

    if (!response.ok) {
      // 422 carries the guard payload (missing COA / unbalanced totals) in
      // data - surface both.
      const message = json.error || json.message || `Request failed: ${response.status}`
      return { data: json.data ?? null, error: message }
    }

    return { data: json.data ?? null }
  } catch (error) {
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/** Reverse a booking but keep the transaction as a Draft (same engine
 * sweep as trash — progeny included). dryRun returns the affected counts
 * for the confirm dialog without changing anything. */
export const unbookAssetTransaction = async (
  transactionId: string,
  accessToken: string | null = null,
  dryRun: boolean = false
): Promise<AssetTransactionActionResponse> => {
  try {
    const url = buildFullUrl(
      BACKEND_BASE_URL,
      API_ROUTES.ASSET_TRANSACTIONS.UNBOOK,
      { id: transactionId },
      dryRun ? { dry_run: 'true' } : {}
    )
    const response = await fetch(url, {
      method: API_ROUTES.ASSET_TRANSACTIONS.UNBOOK.method,
      headers: authHeaders(accessToken)
    })
    const json = (await response.json().catch(() => ({}))) as {
      data?: Record<string, any>
      message?: string
      error?: string
    }

    if (!response.ok) {
      return { data: json.data ?? null, error: json.error || json.message || `Request failed: ${response.status}` }
    }
    return { data: json.data ?? null }
  } catch (error) {
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const trashAssetTransaction = async (
  transactionId: string,
  accessToken: string | null = null,
  dryRun: boolean = false
): Promise<AssetTransactionActionResponse> => {
  try {
    const url = buildFullUrl(
      BACKEND_BASE_URL,
      API_ROUTES.ASSET_TRANSACTIONS.DELETE,
      { id: transactionId },
      dryRun ? { dry_run: 'true' } : {}
    )
    const response = await fetch(url, {
      method: API_ROUTES.ASSET_TRANSACTIONS.DELETE.method,
      headers: authHeaders(accessToken)
    })
    const json = (await response.json().catch(() => ({}))) as {
      data?: Record<string, any>
      message?: string
      error?: string
    }

    if (!response.ok) {
      return { data: json.data ?? null, error: json.error || json.message || `Request failed: ${response.status}` }
    }
    return { data: json.data ?? null }
  } catch (error) {
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}
